import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { CircularProgress, MenuItem, Typography } from '@mui/material';
import { NestedMenuItem } from 'mui-nested-menu';
import { ReactNode, useEffect, useState } from 'react';
import { Dropdown } from './Dropdown';

interface BulkActionOption {
    label: string;
    onClick?: () => void;
    disabled?: () => boolean;
    isLoading?: boolean;
    items?: BulkActionOption[];
}

interface BulkActionsDropdownProps {
    options: BulkActionOption[];
    disabled?: boolean;
    tooltip?: string;
    loading?: boolean;
    selectedCount?: number;
    label?: ReactNode;
    closeWhenLoading?: boolean;
}

const BulkActionMenuItem = ({ option, onActionClick }: { option: BulkActionOption; onActionClick: () => void }) => {
    const disabled = option.disabled?.() || option.isLoading;

    if (option.items) {
        return (
            <NestedMenuItem
                key={`bulk-${option.label}`}
                disabled={disabled}
                className="flex gap-2 justify-between items-center px-4 py-0"
                sx={{
                    position: 'relative',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 16,
                        right: 16,
                        borderBottom: 1,
                        borderColor: 'divider',
                    },
                }}
                parentMenuOpen
                label={option.label}
                rightIcon={<ChevronRightIcon />}
                renderLabel={() => (
                    <div className="flex gap-2 w-full h-full py-[6px]">
                        {option.isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                        <Typography>{option.label}</Typography>
                    </div>
                )}
            >
                {option.items.map((item) => (
                    <BulkActionMenuItem key={`bulk-${item.label}`} option={item} onActionClick={onActionClick} />
                ))}
            </NestedMenuItem>
        );
    }

    return (
        <MenuItem
            disabled={disabled}
            className="px-4 py-0"
            onClick={() => {
                onActionClick();
                option.onClick?.();
            }}
            key={`bulk-${option.label}`}
            sx={{
                position: 'relative',
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 16,
                    right: 16,
                    borderBottom: 1,
                    borderColor: 'divider',
                },
            }}
        >
            <div className="flex gap-2 w-full h-full py-[6px]">
                {option.isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                <Typography>{option.label}</Typography>
            </div>
        </MenuItem>
    );
};

const BulkActionsDropdown = ({
    options,
    disabled = false,
    tooltip,
    loading = false,
    selectedCount = 0,
    label = 'Bulk Actions',
    closeWhenLoading = false,
}: BulkActionsDropdownProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    useEffect(() => {
        if (closeWhenLoading && loading) {
            setAnchorEl(null);
        }
    }, [closeWhenLoading, loading]);

    const closeDropdown = () => setAnchorEl(null);

    return (
        <Dropdown
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={closeDropdown}
            buttonProps={{
                fullWidth: true,
                disabled,
                className: 'h-full',
                startIcon: loading ? <CircularProgress size={18} color="inherit" /> : <SettingsOutlinedIcon />,
                onClick: (event) => setAnchorEl((currentAnchorEl) => (currentAnchorEl ? null : event.currentTarget)),
                children: (
                    <Typography fontFamily="Inter">
                        {label} {selectedCount > 0 && !disabled ? `(${selectedCount})` : ''}
                    </Typography>
                ),
                tooltip,
            }}
        >
            {options.map((option) => (
                <BulkActionMenuItem key={`bulk-${option.label}`} option={option} onActionClick={closeDropdown} />
            ))}
        </Dropdown>
    );
};

export { BulkActionsDropdown };
export type { BulkActionOption };
